// ─────────────────────────────────────────────────────────────────────────────
// Authoritative auth claims from the JWT access token.
//
// Route guards must decide "client portal vs internal app" from the access
// token, NOT from the persisted `user` object. The persisted user can be stale
// or partial (e.g. an older login that pre-dates a field, or a profile written
// without `clientOrgId`), which previously caused a CLIENT_ADMIN to be routed
// into the internal admin shell. The token is the server-issued source of truth
// (the API verifies its signature on every request), so we trust it for routing.
//
// NOTE: this is a non-verifying decode used ONLY for client-side routing/UX.
// All real authorisation happens server-side against the verified token.
// ─────────────────────────────────────────────────────────────────────────────
import type { UserRole } from '@/types';

export interface AuthClaims {
  sub?: string;
  email?: string;
  role?: UserRole;
  clientOrgId?: string | null;
  exp?: number;
}

/** Base64url-decode and JSON-parse a JWT payload. Returns null on any failure. */
export function decodeAccessToken(token: string | null | undefined): AuthClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as AuthClaims;
  } catch {
    return null;
  }
}

/**
 * Resolve whether the current session belongs to a client-portal user.
 * Prefers the token claim; falls back to the persisted user only when the token
 * cannot be decoded.
 */
export function resolveClientOrgId(
  token: string | null | undefined,
  fallbackClientOrgId?: string | null,
): string | null {
  const claims = decodeAccessToken(token);
  if (claims) return claims.clientOrgId ?? null;
  return fallbackClientOrgId ?? null;
}

/** Resolve the effective role, preferring the token claim. */
export function resolveRole(
  token: string | null | undefined,
  fallbackRole?: UserRole | null,
): UserRole | null {
  const claims = decodeAccessToken(token);
  return claims?.role ?? fallbackRole ?? null;
}
